import { useEffect, useMemo, useRef } from 'react'

import { getDefaultPersistentState } from '../../persistentState/config'
import { Store } from '../types'
import { LocalStorage } from '../utils'

import { QuoteV2 } from 'types'
import { useGetQuoteV2Query } from 'app/services'
import useQuoteStore from 'src/store/store'
import { Quote } from 'src/store/types'
import { PossibleFeatureFlags, useFeatureFlags } from 'utils'
import { useAbTests } from 'utils/abTests/abTests'
import { PossibleAbTests } from 'utils/abTests/abTests.types'
import { transformQuoteV2ToPageState } from 'utils/transformStateToDetailsList'
import { useShouldShowPreviousInsuranceCancelled } from 'utils/useShouldShowPreviousInsuranceCancelled'

const transformToPersistentState = ({
  quote,
  showPreviousInsuranceCancelled,
  abTests,
}: {
  quote: QuoteV2
  showPreviousInsuranceCancelled: boolean
  featureFlags?: PossibleFeatureFlags
  abTests?: PossibleAbTests
}) => {
  const state = getDefaultPersistentState()
  state.pageState = transformQuoteV2ToPageState({
    data: quote,
    showPreviousInsuranceCancelled,
    abTests,
  })
  return state
}

export const usePostQuoteEditStore: Store = (quoteId, { location }) => {
  const localStorage = LocalStorage(quoteId)
  const quoteV2 = useGetQuoteV2Query({ quoteId })
  const { preQuoteFeatureFlags } = useFeatureFlags()
  const { preQuoteAbTests } = useAbTests()
  const { showPreviousInsuranceCancelled } = useShouldShowPreviousInsuranceCancelled()
  const isInitialEntry = location.state?.fromOverviewPage || location.state?.fromAccountPage
  const { getQuoteData, createEditQuote } = useQuoteStore()

  const data = useMemo(() => {
    const store = localStorage.get()
    if (!('errorType' in store) && !isInitialEntry) {
      return store
    }
    if (quoteV2.data === undefined || preQuoteFeatureFlags === null) {
      return undefined
    }
    return transformToPersistentState({
      quote: quoteV2.data,
      showPreviousInsuranceCancelled,
      abTests: preQuoteAbTests,
    })
  }, [quoteV2.data, preQuoteFeatureFlags, preQuoteAbTests, isInitialEntry, localStorage])

  const lastSyncedRef = useRef<typeof data | undefined>(undefined)

  useEffect(() => {
    const zustandQuoteData = getQuoteData(quoteId)

    if (data != null && lastSyncedRef.current !== data) {
      createEditQuote(quoteId, {
        ...(data as Partial<Quote>),
        pageState: { ...data.pageState, ...zustandQuoteData },
      })
      lastSyncedRef.current = data
    }
  }, [])

  return {
    data,
    isLoading: quoteV2.isFetching,
    sync: localStorage.sync,
    clear: localStorage.clear,
  }
}
